/// <amd-module name="Core/Medius.Core.Web/Scripts/Medius/apps/inbox/spa"/>
import { applications } from "Core/Medius.Core.Web/Scripts/Medius/apps/spaManager/applications";
import { once } from "underscore";
import { initialize } from "Core/Medius.Core.Web/Scripts/Medius/lockManager";
import { InboxInstance as inbox } from "Core/Medius.Core.Web/Scripts/Medius/apps/inbox/inbox";
import { trigger } from "Core/Medius.Core.Web/Scripts/eventBus";
import * as EventNames from "Core/Medius.Core.Web/Scripts/events/eventNames";
import * as route from "Core/Medius.Core.Web/Scripts/Medius/apps/spaManager/route";
import { renderTemplate } from "Core/Medius.Core.Web/Scripts/Medius/apps/utils";
import { scrollToSelectedGridRow } from "Core/Medius.Core.Web/Scripts/Medius/apps/inbox/helpers";
import { setNewInboxState } from "Core/Medius.Core.Web/Scripts/Candu/canduTracker";

const taskAction = "Tasks";
const documentAction = "Documents";
const documentTaskAction = "DocumentTasks";

class InboxApp {
    public app: any = inbox;
    public name = applications.inbox;
    public routes: any[];

    private documentPreviouslyOpened = false;
    constructor(private spaManager: any, public containerId: string, private canToggleNewInbox: boolean, public isNewInboxEnabled: boolean) {
        inbox.registerSpaApp(this, canToggleNewInbox, isNewInboxEnabled);
        initialize();
        setNewInboxState(isNewInboxEnabled);

        const open = () => {
            if (this.documentPreviouslyOpened) {
                this.documentPreviouslyOpened = false;
                this.app.resetAndOpenFirstTask();
            } else {
                this.app.turnOffFullscreen(false);
                this.app.selectedTasksHandler.isInDocumentMode(false);
                this.app.originPlace(null);
                this.onInitialOpen();
            }

            spaManager.openApp(this);
        };

        const openTask = (taskId: number) => {
            this.refreshIfDocumentPreviouslyOpened();
            this.app.openTask(taskId);
            this.spaManager.openApp(this);
            scrollToSelectedGridRow();
        };

        const openDocument = (documentId: number) => {
            this.setDocumentMode();
            this.app.openDocument(documentId);
            this.spaManager.openApp(this);
        };

        const openDocumentNonFullscreen = (documentId: number) => {
            this.setDocumentMode();
            this.app.openDocumentNonFullscreen(documentId);
            this.spaManager.openApp(this);
        };

        const openTaskInDocumentMode = (taskId: number) => {
            this.setDocumentMode();
            this.app.openTaskInDocumentMode(taskId);
            this.spaManager.openApp(this);
        };

        const openTypeGroup = (typeName: string) => {
            this.refreshIfDocumentPreviouslyOpened();
            this.app.currentTypeGroup(typeName);
            this.app.openTypeGroup();
            this.spaManager.openApp(this);
            scrollToSelectedGridRow();
        };

        const handleInitMine = (hashQuery: string) => {
            if (!this.app.isNewInboxEnabled()) {
                const newUrl = '#/' + taskAction;
                this.redirect(newUrl);
                return;
            }

            const newUrl = '#/' + taskAction + '?' + hashQuery;
            this.notifyNewInbox(newUrl);
            spaManager.openApp(this);
        };

        const handleInitAll = (hashQuery: string) => {
            if (!this.app.isNewInboxEnabled()) {
                const newUrl = '#/' + taskAction + '/ShowDocumentNonFullscreen';
                this.redirect(newUrl);
                return;
            }

            const newUrl = '#/' + taskAction + '/ShowDocumentNonFullscreen' + '?' + hashQuery;
            this.notifyNewInbox(newUrl);
            spaManager.openApp(this);
        };

        const handleMine = (taskId: number, hashQuery: string) => {
            if (!this.app.isNewInboxEnabled()) {
                const newUrl = '#/' + taskAction + '/' + taskId;
                this.redirect(newUrl);
                return;
            }

            const newUrl = '#/' + taskAction + '/' + taskId + '?' + hashQuery;
            this.notifyNewInbox(newUrl);

            this.app.openTask(taskId, false);
            spaManager.openApp(this);
        };

        const handleAll = (documentId: number, hashQuery: string) => {
            if (!this.app.isNewInboxEnabled()) {
                const newUrl = '#/' + taskAction + '/ShowDocument/' + documentId;
                this.redirect(newUrl);
                return;
            }

            const newUrl = '#/' + taskAction + '/ShowDocument/' + documentId + '?' + hashQuery;
            this.notifyNewInbox(newUrl);

            this.setDocumentMode();
            this.app.openDocument(documentId);
            this.spaManager.openApp(this);
        };

        const handleAllDetails = (documentId: number, hashQuery: string) => {
            if (!this.app.isNewInboxEnabled()) {
                const newUrl = '#/' + taskAction + '/ShowDocumentNonFullscreen/' + documentId;
                this.redirect(newUrl);
                return;
            }

            const newUrl =
                '#/' + taskAction + '/ShowDocumentNonFullscreen/' + documentId + '?' + hashQuery;
            this.notifyNewInbox(newUrl);

            this.setDocumentMode();
            this.app.openDocumentNonFullscreen(documentId);
            this.spaManager.openApp(this);
        };

        this.routes = [
            route.create(taskAction, open),
            route.create(taskAction + "/", open),
            route.create(taskAction + "/:taskId", openTask),
            route.create(taskAction + "/:taskId/", openTask),
            route.create(taskAction + "/TypeGroups/:typeName", openTypeGroup),
            route.create(taskAction + "/TypeGroups/:typeName/", openTypeGroup),
            route.create(documentAction + "/:documentId", openDocument),
            route.create(documentAction + "/:documentId/", openDocument),
            route.create(documentTaskAction + "/:taskId", openTaskInDocumentMode),
            route.create(documentTaskAction + "/:taskId/", openTaskInDocumentMode),
            // TODO: remove when old Inbox removed
            route.create(taskAction + "/ShowDocument/:documentId", openDocument),
            route.create(taskAction + "/ShowDocument/:documentId/", openDocument),
            route.create(taskAction + "/ShowDocumentNonFullscreen/:documentId", openDocumentNonFullscreen),
            route.create(taskAction + "/ShowDocumentNonFullscreen/:documentId/", openDocumentNonFullscreen),

            route.create(taskAction + '?:hashQuery', handleInitMine),
            route.create(taskAction + '/:taskId?:hashQuery', handleMine),
            route.create(
                taskAction + '/ShowDocumentNonFullscreen?:hashQuery',
                handleInitAll
            ),
            route.create(taskAction + '/ShowDocument/:documentId?:hashQuery', handleAll),
            route.create(
                taskAction + '/ShowDocumentNonFullscreen/:documentId?:hashQuery',
                handleAllDetails
            )
        ];
    }

    public initialize = once(() => {
        this.app.init();
    });

    public onTaskIdChanged() {
        this.openInbox();
    }

    public onInitialOpen() {
        this.openInbox(false, true);
    }

    private openInbox(withTrigger?: boolean, withReplace?: boolean) {
        const taskId = this.app.currentTaskId();
        const segments: (string | number)[] = [taskAction];

        if (typeof taskId === "number") {
            segments.push(taskId);
        }

        this.spaManager.router.updateUrl(segments, withTrigger, withReplace);

        trigger(EventNames.urlHashChanged);
        this.openInboxApp();
    }

    public openInboxApp() {
        this.spaManager.openApp(this, false);
    }

    private setDocumentMode() {
        this.app.currentTypeGroup(null);
        this.documentPreviouslyOpened = true;
    }

    private refreshIfDocumentPreviouslyOpened() {
        if (this.documentPreviouslyOpened) {
            this.documentPreviouslyOpened = false;
            this.app.resetAndRefresh();
        }
    }

    public redirect(data: any) {
        this.spaManager.router.navigate(data.url);
    }

    private notifyNewInbox(url: string) {
        this.app.communicateUrlChange(url);
    }
}

export function create(spaManager: any, containerId: string, canToggleNewInbox: boolean, isNewInboxEnabled: boolean) {
    return new InboxApp(spaManager, containerId, canToggleNewInbox, isNewInboxEnabled);
}

export function getView() {
    return renderTemplate("inbox-template");
}
